import { ApolloCache, NormalizedCacheObject } from '@apollo/client';
import { G } from '@mobily/ts-belt';
import { Box, Divider, Modal, Spinner, Text } from '@stitch-fix/mode-react';
import { useCallback } from 'react';
import { KeptItemFeedbackFreeformQuestion } from '../../types/definitions/vendor/graphql-schema.d';
import { useToast } from '../../utils/contexts/toast-provider';
import { ShopProductVariantIsHiddenFragmentDoc } from '../../utils/hooks/getBoughtItems';
import { GetRemoveItemFeedbackQuestionsQuery, RemoveItemModalFragment, useGetRemoveItemFeedbackQuestionsQuery } from '../../utils/hooks/getRemoveItemFeedbackQuestions';
import { GetTopItemsDocument } from '../../utils/hooks/getTopItems';
import { useHideItemClientFeedbackMutation } from '../../utils/hooks/hideItemClientFeedbackUpdate';
import { useWardrobeParams } from '../../utils/hooks/useWardrobeParams';
import { useBoughtItemsMachine } from '../../utils/machines/boughtItems/context-provider';
import { RemoveItemForm, RemoveItemFormProps, RemoveItemFormValues } from './RemoveItemForm';
type RemoveItemModalType = {
  skuId: string;
  orderId: string;
  context: string;
  open: boolean;
  handleModalClose: () => void;
};
type MultipleChoiceQuestion = {
  __typename: 'KeptItemFeedbackMultipleChoiceQuestion';
  questionId: string;
  label: string;
  options: MultipleChoiceQuestionOption[];
};
type MultipleChoiceQuestionOption = {
  __typename?: 'KeptItemFeedbackMultipleChoiceQuestionOption';
  questionId: string;
  label: string;
  value: boolean;
};
export type FeedbackQuestion = MultipleChoiceQuestion | KeptItemFeedbackFreeformQuestion;
const updateCacheForShopProductVariant = (cache: ApolloCache<NormalizedCacheObject>, shopProductVariantId: string, isHidden: boolean) => {
  cache.writeFragment({
    id: `ShopProductVariant:${shopProductVariantId}`,
    fragment: ShopProductVariantIsHiddenFragmentDoc,
    data: {
      isHidden
    }
  });
};

/**
 * Guarding against undefined removeBoughtModal data.
 */
const serializeBoughtItemModalData = (data: GetRemoveItemFeedbackQuestionsQuery['client']): RemoveItemModalFragment => {
  if (G.isObject(data)) {
    return data.wardrobe.boughtModule.removeBoughtItemModal;
  }
  return {
    heading: '',
    subheading: '',
    feedbackQuestions: {
      questionSetId: 'none',
      questions: []
    }
  };
};

/**
 * Loading state component
 */
const loadingState = <Box data-testid="remove-items-loading" style={{
  height: 400,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}}>
    <Spinner />
  </Box>;

/**
 * This function de-serialize the feedback question request and
 * return formatted questions to be used in the RemoveItemForm Component
 */
const formatFeedbackQuestions = (res: FeedbackQuestion[]): RemoveItemFormProps['questions'] | undefined => {
  return res.reduce<RemoveItemFormProps['questions']>((formQuestions, currentValue) => {
    if (currentValue.__typename === 'KeptItemFeedbackFreeformQuestion') {
      formQuestions.otherComments.push({
        questionId: currentValue.questionId,
        label: currentValue.secondaryLabel ?? ''
      });
    }
    if (currentValue.__typename === 'KeptItemFeedbackMultipleChoiceQuestion') {
      formQuestions.reasonSelections.push({
        questionId: currentValue.questionId,
        label: currentValue.label,
        options: currentValue.options.map(option => {
          return {
            label: option.label,
            value: option.questionId
          };
        })
      });
    }
    return formQuestions;
  }, {
    otherComments: [],
    reasonSelections: []
  });
};

/**
 * The Remove Item Modal Component renders the Modal to submit feedback to the
 */
export const RemoveItemModal = ({
  skuId,
  orderId,
  context,
  open,
  handleModalClose
}: RemoveItemModalType) => {
  const {
    data,
    loading
  } = useGetRemoveItemFeedbackQuestionsQuery({
    variables: {
      skuId
    },
    skip: !open
  });
  const {
    addToast
  } = useToast();
  const {
    send
  } = useBoughtItemsMachine();
  const {
    setWardrobePageNumberParam
  } = useWardrobeParams();
  const [hideItemClientFeedback] = useHideItemClientFeedbackMutation({
    onCompleted: () => {
      addToast({
        message: 'Item successfully removed',
        status: 'success'
      });
      handleModalClose();
    },
    onError: () => {
      addToast({
        message: 'Oops, Something went wrong',
        status: 'error'
      });
    },
    /**
     * Refetching the top items query to update the top items list in case the
     * removed bought item was also in the top items list.
     */
    refetchQueries: [{
      query: GetTopItemsDocument
    }],
    update: (cache, {
      data: mutationData
    }) => {
      if (!mutationData?.hideItemClientFeedbackUpdate?.result) return;
      updateCacheForShopProductVariant(cache, skuId, true);
      send('REMOVE_BOUGHT_ITEM', {
        removedSkuId: skuId,
        goToPage: setWardrobePageNumberParam
      });
    }
  });
  const {
    heading,
    subheading,
    feedbackQuestions
  } = serializeBoughtItemModalData(data?.client);
  const onSubmit = (answerData: RemoveItemFormValues) => {
    handleHideItem(answerData);
  };
  const handleHideItem = useCallback((answerData: RemoveItemFormValues) => {
    hideItemClientFeedback({
      variables: {
        input: {
          skuId,
          context,
          resourceId: orderId,
          reasons: answerData.reasonSelections,
          comments: answerData.otherComments
        }
      }
    });
  }, [hideItemClientFeedback, skuId, context, orderId]);
  const formQuestions = formatFeedbackQuestions(feedbackQuestions.questions);
  return <Modal heading={heading} isOpen={open} onClose={handleModalClose}>
      {loading && loadingState}

      {!loading && !G.isUndefined(formQuestions) && <>
          <Text height="tall" family="regular" color="red-40" scale="3">
            {subheading}
          </Text>
          <Divider variant="subtle" my={1} />
          <RemoveItemForm questions={formQuestions} onSuccessfulValidation={onSubmit} onCloseModal={handleModalClose} />
        </>}
    </Modal>;
};